import { useState } from "react";
import { Check, ChevronsUpDown, Mail, Phone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import { POC_ROLES } from "@/constants/crm";

function MemberPicker({ team, value, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const sel = team.find((t) => t.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} disabled={disabled} className="flex h-9 w-full items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-700 transition-colors hover:border-zinc-300 disabled:cursor-not-allowed disabled:opacity-60">
          <span className={sel ? "" : "text-zinc-400"}>{sel ? sel.name : "Select employee…"}</span>
          <ChevronsUpDown className="h-4 w-4 text-zinc-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput data-testid={`${testid}-search`} placeholder="Search employee…" />
          <CommandList>
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              {team.map((t) => (
                <CommandItem key={t.id} value={`${t.name} ${t.department || ""}`} data-testid={`${testid}-option-${t.id}`} onSelect={() => { onChange(t); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${t.id === value ? "opacity-100" : "opacity-0"}`} />
                  <div><div className="text-sm">{t.name}</div><div className="text-[11px] text-zinc-400">{t.title} · {t.department}</div></div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function PocPanel({ project, team, update }) {
  const { can } = useAuth();
  const editable = can("POC", "edit") || can("Projects", "edit");
  const poc = project.poc || {};

  const assign = (key, member) => {
    const body = { poc: { ...poc, [key]: member.id } };
    if (key === "lead") body.lead = member.name;
    if (key === "cs") body.cs_spoc = member.name;
    update(project.id, body, `${POC_ROLES.find(([k]) => k === key)[1]} assigned: ${member.name}`);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="poc-panel">
      {POC_ROLES.map(([key, label, desc]) => {
        const m = team.find((t) => t.id === poc[key]);
        return (
          <div key={key} data-testid={`poc-card-${key}`} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
            <div className="text-sm font-bold text-zinc-900">{label}</div>
            <div className="mt-0.5 min-h-[32px] text-xs text-zinc-500">{desc}</div>
            <div className="mt-3"><MemberPicker testid={`poc-picker-${key}`} team={team} value={poc[key]} disabled={!editable} onChange={(t) => assign(key, t)} /></div>
            <div className="mt-3 min-h-[44px] space-y-1 text-xs">
              {m ? (
                <>
                  <div className="flex items-center gap-2 text-zinc-600"><Mail className="h-3.5 w-3.5 text-zinc-400" />{m.email || <span className="text-zinc-400">No email on file</span>}</div>
                  <div className="flex items-center gap-2 text-zinc-600"><Phone className="h-3.5 w-3.5 text-zinc-400" />{m.phone || <span className="text-zinc-400">No phone on file</span>}</div>
                </>
              ) : <div className="text-zinc-400">Not assigned yet.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
